import React, { useEffect, useState } from "react"
import { getAPIBaseURL } from "../services/helpers"
import { getData, postData } from "../services/request"

const STATUS_OPTIONS = [
  { value: "", label: "All applicants" },
  { value: "SUBMITTED", label: "Interview submitted" },
  { value: "UNDER_REVIEW", label: "Under review" },
  { value: "INTERVIEW_IN_PROGRESS", label: "Interview in progress" },
  { value: "SELECTED", label: "Selected" },
  { value: "REJECTED", label: "Rejected" }
]

const EmployerGeneralMessages = () => {
  const [jobs, setJobs] = useState([])
  const [selectedJobId, setSelectedJobId] = useState("")
  const [broadcasts, setBroadcasts] = useState([])
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const loadJobs = async () => {
    setLoading(true)
    setError("")
    const res = await getData(`${getAPIBaseURL()}/employme/employer/messaging/jobs`, {}, {})
    setLoading(false)
    if (res.error === "" && res.result?.status === 200) {
      const list = res.result.data?.data || []
      setJobs(list)
      if (!selectedJobId && list.length) {
        setSelectedJobId(String(list[0]._id))
      }
      return
    }
    setError(res.result?.data?.data || "Unable to load jobs")
  }

  const loadBroadcasts = async (jobId) => {
    if (!jobId) {
      setBroadcasts([])
      return
    }
    const res = await getData(
      `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/messages/broadcasts`,
      {},
      {}
    )
    if (res.error === "" && res.result?.status === 200) {
      setBroadcasts(res.result.data?.data?.broadcasts || [])
    }
  }

  useEffect(() => {
    loadJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedJobId) {
      loadBroadcasts(selectedJobId)
    }
  }, [selectedJobId])

  const handleSend = async () => {
    setError("")
    setSuccess("")
    if (!selectedJobId) {
      setError("Select a job first")
      return
    }
    if (!body.trim()) {
      setError("Message body is required")
      return
    }

    setSending(true)
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${selectedJobId}/messages/broadcast`
    const res = await postData(
      url,
      { "Content-Type": "application/json" },
      JSON.stringify({
        subject: subject.trim() || undefined,
        body: body.trim(),
        statusFilter: statusFilter || undefined
      })
    )
    setSending(false)

    if (res.error === "" && (res.result?.status === 200 || res.result?.status === 201)) {
      setSuccess(res.result.data?.data?.message || "Broadcast sent")
      setBody("")
      setSubject("")
      loadBroadcasts(selectedJobId)
      loadJobs()
      return
    }
    setError(res.result?.data?.data || "Unable to send broadcast")
  }

  const selectedJob = jobs.find((j) => String(j._id) === String(selectedJobId))

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4">
          <h3 className="text-base font-semibold text-gray-900">Send general message</h3>
          <p className="mt-1 text-xs text-gray-600">
            Broadcast one update to every applicant on a job (optionally filtered by status).
          </p>
        </div>
        <div className="space-y-3 p-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>
          )}
          {success && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
              {success}
            </div>
          )}

          <label className="block text-xs font-medium text-gray-700">
            Job
            <select
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
            >
              {jobs.map((job) => (
                <option key={job._id} value={job._id}>
                  {job.title} ({job.applicantCount} applicants)
                </option>
              ))}
            </select>
          </label>

          {selectedJob && (
            <p className="text-[11px] text-gray-500">
              {selectedJob.applicantCount} total applicants · {selectedJob.broadcastCount} past broadcast(s)
            </p>
          )}

          <label className="block text-xs font-medium text-gray-700">
            Filter recipients
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-medium text-gray-700">
            Subject (optional)
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
              placeholder="e.g. Interview timeline update"
            />
          </label>

          <label className="block text-xs font-medium text-gray-700">
            Message
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
              placeholder="Write a message that will appear on each candidate's application dashboard and be sent by email..."
            />
          </label>

          <button
            type="button"
            onClick={handleSend}
            disabled={sending || loading}
            className="w-full rounded-md bg-black px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
          >
            {sending ? "Sending..." : "Send to all matching applicants"}
          </button>
        </div>
      </div>

      <div className="lg:col-span-3 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4">
          <h3 className="text-base font-semibold text-gray-900">Broadcast history</h3>
          <p className="mt-1 text-xs text-gray-600">Previous general messages for the selected job.</p>
        </div>
        <div className="p-4">
          {loading && <p className="text-sm text-gray-500">Loading...</p>}
          {!loading && !broadcasts.length && (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
              No general broadcasts sent for this job yet.
            </div>
          )}
          <ul className="space-y-3">
            {broadcasts.map((b, idx) => (
              <li key={b._id || idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{b.subject || "General update"}</p>
                    <p className="mt-1 text-xs text-gray-600 whitespace-pre-line">{b.body}</p>
                  </div>
                  <div className="shrink-0 text-right text-[10px] text-gray-500">
                    <p>{b.recipientCount ?? 0} recipients</p>
                    <p>{b.createdAt ? new Date(b.createdAt).toLocaleString() : ""}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default EmployerGeneralMessages
